import { navigate } from '@reach/router';
import { FormikProps } from 'formik';
import { keyBy } from 'lodash';
import React, { useContext, useMemo } from 'react';
import { userHasPermission } from '../../../authorisation/permission';
import { useOnMount } from '../../../helpers/hooks/useOnMount';
import { parseNumberOrNull } from '../../../helpers/stringHelpers';
import { PrimaryProgressButton, SecondaryButton } from '../../../shared/Button';
import { Form, FormButtonRow, FormHeaderFieldsContainer } from '../../../shared/form/Form';
import { SelectField } from '../../../shared/form/inputs/SelectField';
import { UserContext } from '../../../users/UserContext';
import { HeatPumpBillOfMaterialsFields } from '../../bill-of-materials/HeatPumpBillOfMaterialsFields';
import { SolarBillOfMaterialsFields } from '../../bill-of-materials/SolarBillOfMaterialsFields';
import { UnderfloorHeatingBillOfMaterialsFields } from '../../bill-of-materials/UnderfloorHeatingBillOfMaterialsFields';
import { projectUrls } from '../../projects/projectUrls';
import { DataForViewOrEditQuoteBillOfMaterialsForm } from './dataForViewOrEditQuoteBillOfMaterialsForm';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from './viewOrEditQuoteBillOfMaterialsFormModel';
import { GuidanceTextFieldSection } from './GuidanceTextFieldSection';
import { BillOfMaterialsContext } from '../../bill-of-materials/BillOfMaterialsContext';

export type NextStepAfterSavingBillOfMaterials = 'BackToProject' | 'UploadToNetSuite';

type Props = {
  projectId: number;
  data: DataForViewOrEditQuoteBillOfMaterialsForm;
  setNextStepAfterSave: (nextStep: NextStepAfterSavingBillOfMaterials) => void;
} & FormikProps<ViewOrEditQuoteBillOfMaterialsFormModel>;

export const ViewOrEditQuoteBillOfMaterialsForm = (props: Props) => {
  const { getUser } = useContext(UserContext);
  const user = getUser();
  const isFormDisabled = props.data.isSubmitted || !userHasPermission({ user, permission: 'Edit' });

  const guidanceTextTemplatesById = useMemo(
    () => keyBy(props.data.guidanceTextTemplates, gtt => gtt.guidanceTextTemplateId),
    [props.data.guidanceTextTemplates],
  );

  useOnMount(() => {
    props.validateForm(props.values);
  });

  const customerTypeOptionsForSelectField: Array<{
    value: number | null;
    text: string;
  }> = props.data.customerTypeOptions.map(option => ({
    value: option.id,
    text: option.name,
  }));

  customerTypeOptionsForSelectField.splice(0, 0, { value: null, text: '-- Select a Customer Type --' });

  return (
    <Form disabled={isFormDisabled}>
      <BillOfMaterialsContext.Provider
        value={{
          isUsingLegacyPricingModel: props.data.isUsingLegacyPricingModel,
          customerTypeOptions: props.data.customerTypeOptions,
        }}
      >
        {!props.data.isUsingLegacyPricingModel && (
          <FormHeaderFieldsContainer>
            <SelectField name="customerTypeId" label="Customer Type" options={customerTypeOptionsForSelectField} />
          </FormHeaderFieldsContainer>
        )}
        {props.data.hasUnderfloorHeating && (
          <>
            <UnderfloorHeatingBillOfMaterialsFields
              discountPercentage={props.values.underfloorHeatingDiscountPercentage}
              manualDiscountAmount={parseNumberOrNull(props.values.underfloorHeatingManualDiscountAmount)}
              manualDiscountTypeCode={props.values.underfloorHeatingManualDiscountTypeCode}
              units={props.values.units}
              controlOptionalExtras={props.values.underfloorHeatingControlOptionalExtras}
              floorConstructionOptionalExtras={props.values.underfloorHeatingFloorConstructionOptionalExtras}
              cylinderOptionalExtras={props.values.underfloorHeatingCylinderOptionalExtras}
              partsById={props.data.partsById}
              setFieldValue={props.setFieldValue}
              setFieldTouched={props.setFieldTouched}
              errors={props.errors}
              isQuote={true}
            />
          </>
        )}
        {props.data.hasHeatPump && (
          <HeatPumpBillOfMaterialsFields
            discountPercentage={props.values.heatPumpDiscountPercentage}
            manualDiscountAmount={parseNumberOrNull(props.values.heatPumpManualDiscountAmount)}
            manualDiscountTypeCode={props.values.heatPumpManualDiscountTypeCode}
            units={props.values.units}
            heatPumpOptionalExtras={props.values.heatPumpOptionalExtras}
            partsById={props.data.partsById}
            setFieldValue={props.setFieldValue}
            setFieldTouched={props.setFieldTouched}
            errors={props.errors}
            isQuote={true}
          />
        )}
        {props.data.hasSolar && (
          <SolarBillOfMaterialsFields
            discountPercentage={props.values.solarDiscountPercentage}
            manualDiscountAmount={parseNumberOrNull(props.values.solarManualDiscountAmount)}
            manualDiscountTypeCode={props.values.solarManualDiscountTypeCode}
            units={props.values.units}
            solarOptionalExtras={props.values.solarOptionalExtras}
            partsById={props.data.partsById}
            setFieldValue={props.setFieldValue}
            setFieldTouched={props.setFieldTouched}
            errors={props.errors}
            isQuote={true}
          />
        )}
        {props.data.hasUnderfloorHeating && (
          <GuidanceTextFieldSection
            title="Underfloor Heating Guidance Text"
            fieldName="underfloorHeatingGuidanceText"
            guidanceTextFormModels={props.values.underfloorHeatingGuidanceText}
            guidanceTextTemplatesById={guidanceTextTemplatesById}
          />
        )}
        {props.data.hasHeatPump && (
          <GuidanceTextFieldSection
            title="Heat Pump Guidance Text"
            fieldName="heatPumpGuidanceText"
            guidanceTextFormModels={props.values.heatPumpGuidanceText}
            guidanceTextTemplatesById={guidanceTextTemplatesById}
          />
        )}
        {props.data.hasSolar && (
          <GuidanceTextFieldSection
            title="Solar Guidance Text"
            fieldName="solarGuidanceText"
            guidanceTextFormModels={props.values.solarGuidanceText}
            guidanceTextTemplatesById={guidanceTextTemplatesById}
          />
        )}
        <FormButtonRow $align="left">
          {isFormDisabled ? (
            <SecondaryButton onClick={() => navigate(projectUrls.projectDetails(props.projectId))}>
              Back
            </SecondaryButton>
          ) : (
            <>
              <PrimaryProgressButton
                type="submit"
                inProgress={props.isSubmitting}
                onClick={() => props.setNextStepAfterSave('BackToProject')}
              >
                Save
              </PrimaryProgressButton>
              <PrimaryProgressButton
                type="submit"
                inProgress={props.isSubmitting}
                onClick={() => props.setNextStepAfterSave('UploadToNetSuite')}
              >
                Save and Upload
              </PrimaryProgressButton>
            </>
          )}
        </FormButtonRow>
      </BillOfMaterialsContext.Provider>
    </Form>
  );
};
